import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { EvolutionsArrowIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { clsx } from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

export const EvolutionsScrollButton = ({
  direction,
  onClick,
  className = "",
  hide = false,
}: {
  direction: "left" | "right";
  onClick: () => void;
  className?: string;
  hide?: boolean;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "absolute top-[26.5px] -translate-y-1/2 z-20 h-7 w-7 rounded-full bg-action-tertiary shadow-lg",
      "flex items-center justify-center transition-all duration-200",
      "hover:scale-110 hover:bg-action-tertiary",
      "disabled:opacity-0 disabled:pointer-events-none",
      direction === "left" ? "-left-2" : "-right-2",
      className,
      hide && "-z-1",
    )}
  >
    <span className="h-full w-full text-white flex items-center justify-center">
      {direction === "left" ? (
        <EvolutionsArrowIcon className="rotate-180" />
      ) : (
        <EvolutionsArrowIcon />
      )}
    </span>
  </button>
);

export const EvolutionsScrollContainer = ({
  children,
  expanded,
}: {
  children: React.ReactNode;
  expanded: boolean;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (scrollContainer) {
      const scrollLeft = Math.round(scrollContainer.scrollLeft);
      const scrollWidth = Math.round(scrollContainer.scrollWidth);
      const clientWidth = Math.round(scrollContainer.clientWidth);

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  const scroll = (direction: "left" | "right") => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (scrollContainer) {
      const scrollAmount = 700;
      scrollContainer.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current?.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);

      resizeObserverRef.current = new ResizeObserver(handleScroll);

      resizeObserverRef.current.observe(scrollContainer);
      handleScroll();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }
  }, [children]);

  const buttonTop = expanded ? "top-1/2" : "top-[24px]";

  return (
    <div className="relative group min-h-12 touch-device:min-h-16">
      <ScrollArea ref={scrollContainerRef} className="relative flex gap-y-2">
        <div className="flex flex-row gap-x-4 pb-4">{children}</div>
        <ScrollBar
          orientation="horizontal"
          className={clsx(
            "overflow-y-hidden cursor-grab bg-background",
            isMobile && "hidden",
          )}
          forceMount={true}
          scrollBarColor="bg-gray-750"
        />
      </ScrollArea>

      <div
        className={cn(
          "absolute right-0 top-0 bottom-4 w-[146px] pointer-events-none z-10",
          !canScrollRight ? "hidden" : "hidden desktop:flex",
          "bg-[linear-gradient(90deg,rgba(42,41,41,0)_0%,rgba(42,41,41,0.63)_49%,rgba(42,41,41,1)_100%)]",
        )}
      />

      <div
        className={cn(
          "absolute left-0 top-0 bottom-4 w-[146px] pointer-events-none z-10",
          !canScrollLeft ? "hidden" : "hidden desktop:flex",
          "bg-[linear-gradient(90deg,rgba(42,41,41,1)_0%,rgba(42,41,41,0.63)_49%,rgba(42,41,41,0)_100%)]",
        )}
      />
      <EvolutionsScrollButton
        direction="left"
        onClick={() => scroll("left")}
        hide={!canScrollLeft}
        className={cn(
          buttonTop,
          "hidden desktop:flex",
          !canScrollLeft ? "opacity-0" : "opacity-100",
        )}
      />
      <EvolutionsScrollButton
        direction="right"
        onClick={() => scroll("right")}
        hide={!canScrollRight}
        className={cn(
          buttonTop,
          "hidden desktop:flex",
          !canScrollRight ? "opacity-0" : "opacity-100",
        )}
      />
    </div>
  );
};
